import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useSystemBranding } from '@/hooks/useSystemBranding'
import { Btn } from '@/components/ui/Btn'
import { Field } from '@/components/ui/Field'

export default function LoginPage() {
  const { signIn, loading } = useAuth()
  const { data: branding } = useSystemBranding()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [err, setErr] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr('')
    try {
      await signIn(email.trim(), password)
      navigate('/dashboard')
    } catch (e: unknown) {
      const code = (e as { code?: string }).code
      if (code === 'auth/user-disabled') {
        setErr('Conta bloqueada. Fale com o suporte.')
      } else if (code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        setErr('Email ou senha incorretos.')
      } else if (code === 'auth/too-many-requests') {
        setErr('Muitas tentativas. Tente novamente em alguns minutos.')
      } else {
        setErr('Erro ao entrar. Tente novamente.')
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-5 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_90%_60%_at_50%_-10%,rgba(212,168,87,0.18),transparent_70%)]" />

      <form onSubmit={submit} className="w-full max-w-md relative animate-fade-in">
        <div className="text-center mb-8">
          {/* Logo do branding (white-label) ou o logo padrão EventPro */}
          <img
            src={branding?.logo_url || '/logo-eventpro.png'}
            alt={branding?.nome_sistema || 'EventPro Manager'}
            className="h-32 mx-auto mb-3 object-contain rounded-2xl"
          />
          {!branding?.logo_url && (
            <p className="text-text-secondary text-sm font-semibold">Sistema de Gestão de Eventos</p>
          )}
        </div>

        <div className="bg-card border border-border rounded-3xl p-9 shadow-2xl">
          <h2 className="text-lg font-extrabold mb-6">Acessar o sistema</h2>

          <div className="space-y-4">
            <Field
              label="Email"
              value={email}
              onChange={setEmail}
              type="email"
              placeholder="voce@empresa.com"
              autoComplete="email"
              required
            />
            <div className="relative">
              <Field
                label="Senha"
                value={password}
                onChange={setPassword}
                type={show ? 'text' : 'password'}
                placeholder="sua senha"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShow(s => !s)}
                aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute right-3 bottom-2.5 text-text-muted hover:text-text-primary"
              >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {err && (
            <div className="mt-4 bg-danger/10 border border-danger/40 rounded-lg px-3 py-2 text-danger text-xs font-bold">
              ⚠️ {err}
            </div>
          )}

          <Btn type="submit" loading={loading} className="w-full mt-5 py-3 text-base">
            🔐 Entrar
          </Btn>

          <div className="text-center mt-5 space-y-1.5">
            <Link to="/recuperar-senha" className="block text-sm text-accent font-bold hover:underline">
              ✨ Primeiro acesso? Crie sua senha
            </Link>
            <Link to="/recuperar-senha" className="block text-xs text-text-secondary hover:text-accent">
              Esqueci minha senha
            </Link>
          </div>
        </div>

        <p className="text-center text-text-muted text-xs mt-5">
          Ainda não tem conta?{' '}
          <a href="https://eventosonix.com.br" className="text-accent font-bold">
            Conheça os planos
          </a>
        </p>
      </form>
    </div>
  )
}
